import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap } from 'rxjs';
import { LocalDate } from '@js-joda/core';
import { CancelTaskRequest, CreateTaskDefinitionRequest, ModifyTaskDefinitionRequest } from '@core/models/request/restriction.request';
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { PersistencyService } from './persistency.service';
import { ExecutionEnforcementResponse, SchoolEnforcementResponse, TaskDefinitionResponse, TaskExecutionResponse } from '@core/models/response/restriction.response';
import { buildApiEndpoint, handleError } from '@core/utils/service';
import { buildTaskDefinitionFromResponse, buildTaskExecutionFromResponse } from '@core/converter/restriction.converter';
import { TaskDefinition, TaskDefinitionStatus, TaskExecution, TaskExecutionStatus, ParentTaskCancellation } from '@core/models/domain/restriction.domain';
import { DigiResponse } from '@core/models/response/core';
import { getEndOfDay } from '@core/utils/utils';

/**
 * RestrictionService
 *
 * This service provides methods for managing task definitions and executions, including loading, creating, updating, and canceling tasks.
 * It facilitates communication with the backend for task-related operations.
 *
 * Key Features:
 * - Loads active task definitions and executions.
 * - Creates and updates task definitions.
 * - Cancels tasks based on various criteria.
 */
@Injectable({
  providedIn: "root",
})
export class RestrictionService {
  TASK_DEFINITION_API_URI: string;
  TASK_EXECUTION_API_URI: string
  
  constructor(private http: HttpClient, private apiService: ApiService, private persistencyService: PersistencyService) { 
    this.TASK_DEFINITION_API_URI = this.apiService.getApiURL("RestrictionService", "task-definition");
    this.TASK_EXECUTION_API_URI = this.apiService.getApiURL("RestrictionService", "task-execution");
  }

  /**
   * Loads active task definitions by sending a GET request to the server.
   *
   * This function filters task definitions based on their statuses and maps the response to an array of `TaskDefinition` objects using the `buildTaskDefinitionFromResponse` converter.
   * Logs a warning if the response body is invalid or not an array, and returns an empty array in such cases.
   *
   * @param {TaskDefinitionStatus[]} statuses - An array of statuses to filter task definitions.
   * @returns {Observable<TaskDefinition[]>} An observable that emits an array of `TaskDefinition` objects or an empty array in case of an invalid response.
   */
  loadTaskDefinitions(statuses: TaskDefinitionStatus[]): Observable<TaskDefinition[]> {
    return this.http
      .get<DigiResponse<TaskDefinitionResponse[]>>(buildApiEndpoint(this.TASK_DEFINITION_API_URI, `get-school-tasks/${statuses}`), { observe: 'response' })
      .pipe(
        map((response) => {
          const digiResponse = response.body!;
          if (digiResponse.statusCode.status === "OK" && digiResponse.body && Array.isArray(digiResponse.body)) {
            console.log("Task definitions loaded successfully:", digiResponse.body);
            return digiResponse.body.map(buildTaskDefinitionFromResponse);
          }
          console.warn("Invalid task definitions response", digiResponse);
          return [];
        }),
        catchError((error) => handleError<TaskDefinition[]>("Loading task definitions", error))
      );
  }

  /**
   * Loads task executions for a specific date by sending a GET request to the server.
   *
   * This function maps the response to an array of `TaskExecution` objects using the `buildTaskExecutionFromResponse` converter.
   * Logs a warning if the response body is invalid or not an array, and returns an empty array in such cases.
   *
   * @param {LocalDate} date - The date for which task executions are to be loaded.
   * @returns {Observable<TaskExecution[]>} An observable that emits an array of `TaskExecution` objects or an empty array in case of an invalid response.
   */
  loadTaskExecutions(date: LocalDate): Observable<TaskExecution[]> {
    const endpoint = buildApiEndpoint(this.TASK_EXECUTION_API_URI, `get-school-tasks/${date.toString()}`);
    console.log(`🔵 Loading task executions for date: ${date.toString()}`);
    console.log(`🔵 API Endpoint: ${endpoint}`);
    
    return this.http
      .get<DigiResponse<TaskExecutionResponse[]>>(endpoint, { observe: 'response' })
      .pipe(
        map((response) => {
          const digiResponse = response.body!;
          console.log(`🟢 Raw API response for ${date.toString()}:`, digiResponse);
          
          if (digiResponse.statusCode.status === "OK" && digiResponse.body && Array.isArray(digiResponse.body)) {
            console.log(`✅ Task executions received (count: ${digiResponse.body.length}):`, digiResponse.body);
            const converted = digiResponse.body.map(buildTaskExecutionFromResponse);
            console.log(`✅ Converted task executions:`, converted);
            return converted;
          }
          console.warn(`⚠️ Invalid task executions response for ${date.toString()}:`, digiResponse);
          return [];
        }),
        catchError((error) => {
          console.error(`❌ Error loading task executions for ${date.toString()}:`, error);
          return handleError<TaskExecution[]>("Loading task executions", error);
        })
      );
  }

  /**
   * Creates a new task definition by sending a POST request to the server.
   *
   * This function maps the response to a `TaskDefinition` object if the operation is successful.
   * Logs the added task definition details and throws an error if no data is found in the response.
   *
   * @param {CreateTaskDefinitionRequest} request - The request object containing the task definition creation data.
   * @returns {Observable<TaskDefinition>} An observable that emits the created `TaskDefinition` object or throws an error if the operation fails.
   */
  createTaskDefinition(request: CreateTaskDefinitionRequest): Observable<TaskDefinition> {
    return this.http
      .post<DigiResponse<TaskDefinitionResponse>>(buildApiEndpoint(this.TASK_DEFINITION_API_URI, "create"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("Task definition added successfully:", loqtResponse.body);
            return buildTaskDefinitionFromResponse(loqtResponse.body);
          }
          throw new Error("No task definition data found in response");
        }),
        catchError((error) => handleError<TaskDefinition>("Create task definition", error))
      );
  }

  /**
   * Updates an existing task definition by sending a PUT request to the server.
   *
   * This function maps the response to a `TaskDefinition` object if the operation is successful.
   * Logs the updated task definition details and throws an error if no data is found in the response.
   *
   * @param {ModifyTaskDefinitionRequest} request - The request object containing the task definition modification data.
   * @returns {Observable<TaskDefinition | null>} An observable that emits the updated `TaskDefinition` object or throws an error if the operation fails.
   */
  updateTaskDefinition(request: ModifyTaskDefinitionRequest): Observable<TaskDefinition | null> {
    return this.http
      .put<DigiResponse<TaskDefinitionResponse>>(buildApiEndpoint(this.TASK_DEFINITION_API_URI, "modify"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("Task definition modify successfully:", loqtResponse.body);
            return buildTaskDefinitionFromResponse(loqtResponse.body);
          }
          throw new Error("No task definition data found in response");
        }),
        catchError((error) => handleError<TaskDefinition>("Modify task definition", error))
      );
  }

  /**
   * Cancels a task based on the provided request.
   *
   * This function determines the cancellation method based on the request properties and returns the result of the appropriate cancellation method.
   *
   * @param {CancelTaskRequest} request - The request object containing task cancellation details.
   * @returns {Observable<string>} An observable that emits the result of the cancellation operation.
   */
  cancelTask(request: CancelTaskRequest): Observable<string> {
    if (request.taskExecutionId !== undefined) {
        // Check if task is in progress
        if (request.taskExecutionStatus === TaskExecutionStatus.IN_PROGRESS) {
            return this.cancelInProgressTask(request.taskDefinitionId, request.taskExecutionId);
        }
        // Otherwise cancel not started task
        return this.cancelNotStartedTask(request.taskDefinitionId, request.taskExecutionId);
    }
    else {
       if (request.deactivate) {
          return this.deactivateTaskDefinition(request.taskDefinitionId, request.deactivateFromDate);
      }
      else {
          return this.cancelNotCreatedTask(request.taskDefinitionId, request.dateToCancel);
      }
  }
    // return this.deactivateTaskDefinition(request.taskDefinitionId, request.deactivateFromDate);
   
  }

  /**
   * Makes a generic PUT request to the server.
   *
   * This function maps the response to the specified type if the operation is successful.
   * Logs the operation details and throws an error if no data is found in the response.
   *
   * @template T
   * @param {string} endpoint - The API endpoint for the PUT request.
   * @param {any} request - The request object containing the data to be sent.
   * @param {string} operationName - The name of the operation being performed.
   * @returns {Observable<T | null>} An observable that emits the result of the operation or null if the operation fails.
   */
  private makePutRequest<T>(endpoint: string, request: any, operationName: string): Observable<T | null> {
    console.log(`🔵 ${operationName} - Endpoint:`, endpoint);
    console.log(`🔵 ${operationName} - Request:`, request);
    
    return this.http
      .put<DigiResponse<T>>(endpoint, request, { observe: 'response' })
      .pipe(
        map((response) => {
          console.log(`🟢 ${operationName} - Full HTTP response:`, response);
          const loqtResponse = response.body!;
          console.log(`🟢 ${operationName} - loqtResponse:`, loqtResponse);
          
          // TODO: This is a temporary fix - backend should return consistent responses
          // Skip body check for "Cancel in-progress task" as backend doesn't return a body
          if (operationName === "Cancel in-progress task" && loqtResponse.statusCode.status === "OK") {
            console.log(`✅ ${operationName} successfully (no body expected)`);
            return null;
          }
          
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log(`✅ ${operationName} successfully - Response body:`, loqtResponse.body);
            return loqtResponse.body;
          }
          throw new Error(`No data found in response for ${operationName}`);
        }),
        catchError((error) => {
          console.error(`❌ ${operationName} - Error:`, error);
          return handleError<T>(operationName, error);
        })
      );
  }

  /**
   * Deactivates a task definition starting from a specific date.
   *
   * This function sends a PUT request to the server with the deactivation details and maps the response to a string if the operation is successful.
   *
   * @param {string} taskDefinitionId - The ID of the task definition to be deactivated.
   * @param {LocalDate} deactivateFromDate - The date from which the task definition is to be deactivated.
   * @returns {Observable<string | null>} An observable that emits the result of the deactivation operation or null if the operation fails.
   */
  private deactivateTaskDefinition(taskDefinitionId: string, deactivateFromDate: LocalDate): Observable<string | null> {
    const request = {
      taskDefinitionId: taskDefinitionId,
      fromDate: getEndOfDay(deactivateFromDate.minusDays(1)) // Deactivate from the end of the previous day
    }
    return this.makePutRequest<string>(
      buildApiEndpoint(this.TASK_DEFINITION_API_URI, "deactivate"),
      request,
      "Deactivate task definition"
    );
  }

  /**
   * Cancels a task definition for a specific date.
   *
   * This function sends a PUT request to the server with the cancellation details and maps the response to a string if the operation is successful.
   *
   * @param {string} taskDefinitionId - The ID of the task definition to be canceled.
   * @param {LocalDate} dateToCancel - The date for which the task definition is to be canceled.
   * @returns {Observable<string | null>} An observable that emits the result of the cancellation operation or null if the operation fails.
   */
  private cancelNotCreatedTask(taskDefinitionId: string, dateToCancel: LocalDate): Observable<string | null> {
    const request = {
      taskDefinitionId: taskDefinitionId,
      dateToCancel: dateToCancel
    }
    return this.makePutRequest<string>(
      buildApiEndpoint(this.TASK_DEFINITION_API_URI, "cancel-task-execution"),
      request,
      "Cancel task definition"
    );
  }

  /**
   * Cancels a task execution that has not started yet.
   *
   * This function sends a PUT request to the server with the cancellation details and maps the response to a string if the operation is successful.
   *
   * @param {string} taskDefinitionId - The ID of the task definition associated with the task execution.
   * @param {string} taskExecutionId - The ID of the task execution to be canceled.
   * @returns {Observable<string | null>} An observable that emits the result of the cancellation operation or null if the operation fails.
   */
  private cancelNotStartedTask(taskDefinitionId: string, taskExecutionId: string): Observable<string | null> {
    const request = {
      taskDefinitionId: taskDefinitionId,
      taskExecutionId: taskExecutionId
    }
    return this.makePutRequest<string>(
      buildApiEndpoint(this.TASK_EXECUTION_API_URI, "cancel-notstarted-task"),
      request,
      "Cancel task definition"
    );
  }

  /**
   * Cancels a task execution that is currently in progress.
   *
   * This function sends a PUT request to the server with the cancellation details and maps the response to a string if the operation is successful.
   *
   * @param {string} taskDefinitionId - The ID of the task definition associated with the task execution.
   * @param {string} taskExecutionId - The ID of the task execution to be canceled.
   * @returns {Observable<string | null>} An observable that emits the result of the cancellation operation or null if the operation fails.
   */
  private cancelInProgressTask(taskDefinitionId: string, taskExecutionId: string): Observable<string | null> {
    const request = {
      taskDefinitionId: taskDefinitionId,
      taskExecutionId: taskExecutionId
    }
    console.log('🔴 Canceling in-progress task - Request:', request);
    return this.makePutRequest<string>(
      buildApiEndpoint(this.TASK_EXECUTION_API_URI, "cancel-restriction"),
      request,
      "Cancel in-progress task"
    ).pipe(
      tap((response) => console.log('✅ Cancel in-progress task - Response:', response)),
      catchError((error) => {
        console.error('❌ Cancel in-progress task - Error:', error);
        throw error;
      })
    );
  }

  /**
   * What the pupils' devices did about one execution (DIGI-1724).
   *
   * Returns null rather than an empty shape when the backend cannot answer, so the card can stay
   * silent instead of showing three zeroes, which would read as "nothing was shielded".
   */
  loadExecutionEnforcement(taskExecutionId: string): Observable<ExecutionEnforcementResponse | null> {
    return this.http
      .get<DigiResponse<ExecutionEnforcementResponse>>(
        buildApiEndpoint(this.TASK_EXECUTION_API_URI, `enforcement/${encodeURIComponent(taskExecutionId)}`),
        { observe: 'response' }
      )
      .pipe(
        map((response) => {
          const digiResponse = response.body!;
          return digiResponse.statusCode.status === 'OK' && digiResponse.body ? digiResponse.body : null;
        }),
        catchError((error) => handleError<ExecutionEnforcementResponse | null>('Loading execution enforcement', error))
      );
  }

  /** What the pupils' devices did about the school's restrictions on one day, per year group. */
  loadSchoolEnforcement(date: LocalDate): Observable<SchoolEnforcementResponse | null> {
    return this.http
      .get<DigiResponse<SchoolEnforcementResponse>>(
        buildApiEndpoint(this.TASK_EXECUTION_API_URI, `enforcement/school/${date.toString()}`),
        { observe: 'response' }
      )
      .pipe(
        map((response) => {
          const digiResponse = response.body!;
          return digiResponse.statusCode.status === 'OK' && digiResponse.body ? digiResponse.body : null;
        }),
        catchError((error) => handleError<SchoolEnforcementResponse | null>('Loading school enforcement', error))
      );
  }

  loadParentCancellations(childId: string): Observable<ParentTaskCancellation[]> {
    return this.http
      .get<DigiResponse<ParentTaskCancellation[]>>(
        buildApiEndpoint(this.TASK_EXECUTION_API_URI, `parent-cancellations?childId=${encodeURIComponent(childId)}`),
        { observe: 'response' }
      )
      .pipe(
        map((response) => {
          const digiResponse = response.body!;
          if (digiResponse.statusCode.status === 'OK' && digiResponse.body && Array.isArray(digiResponse.body)) {
            return digiResponse.body;
          }
          return [];
        }),
        catchError((error) => handleError<ParentTaskCancellation[]>('Loading parent cancellations', error))
      );
  }

  loadParentCancellationsBySchool(): Observable<ParentTaskCancellation[]> {
    return this.http
      .get<DigiResponse<ParentTaskCancellation[]>>(
        buildApiEndpoint(this.TASK_EXECUTION_API_URI, 'parent-cancellations/school'),
        { observe: 'response' }
      )
      .pipe(
        map((response) => {
          const digiResponse = response.body!;
          if (digiResponse.statusCode.status === 'OK' && digiResponse.body && Array.isArray(digiResponse.body)) {
            return digiResponse.body;
          }
          return [];
        }),
        catchError((error) => handleError<ParentTaskCancellation[]>('Loading school parent cancellations', error))
      );
  }

}